import { useEffect, useRef, useState } from "react";
import useAuth from "hooks/useAuth";
import useWebotServer from "hooks/useWebotServer";
import type { REST_schema } from "../../../../@types/sharedSchema";

/**
 * THE TAG FIELD'S SUGGESTIONS — the realtor's own tags, most used first, asked of the server
 * each time the field's list opens.
 *
 * ── What it replaces ────────────────────────────────────────────────────────────────
 *
 * A stored list. A Cloud Function (`updateUserContactTags`, kept deployed until the contacts
 * migration is finished, read by no current code) appended the tags of EVERY written
 * contact, of every customer, to `contact-tags/{email}.distinctTags`; the field listened to
 * that document, stayed disabled until its first snapshot, and carried a delete button per
 * suggestion because the list could only grow. Measured 2026-10-11: 307 of 2,573 accounts
 * have any tag at all and 77,132 of 5,166,325 contacts carry one (the median account that
 * uses tags has 2 of them, the largest 362) — a trigger on every contact write, while 88% of
 * accounts have not one tag to list. And the list stops moving the day an owner's contacts are
 * cut over to Postgres: their contact documents are frozen, so nothing fires the trigger.
 *
 * ── What it is now ──────────────────────────────────────────────────────────────────
 *
 * No stored list, no listener, no job. `POST /contacts/tags` counts the tags on the caller's
 * live contacts — one statement over the Postgres mirror, at most 500 tags — and this hook
 * asks it ONCE PER OPENING of the list:
 *
 *  - on open, never on mount: the field sits in every contact form, and most are never opened;
 *  - every opening asks again, so a tag saved a moment ago is offered the next time, and a tag
 *    that no contact carries any more is simply gone — which is why the delete button went
 *    with the list it edited;
 *  - an answer belongs to the opening that asked for it. One that lands after the list closed,
 *    after the field unmounted or after a sign-in as someone else is dropped, and the next
 *    opening asks for its own;
 *  - nothing waits for it. The field is free text and usable from its first frame; until the
 *    first answer there are simply no suggestions. The last answer stays with the field
 *    between openings, so a list that is opened again does not blink while it is refreshed;
 *  - a failed ask changes nothing on screen: one console line, no toast, and the field keeps
 *    whatever it had — nothing, on a first opening.
 *
 * The field holds no spinner any more, for the same reason: there is nothing to wait for.
 */

const NO_SUGGESTIONS: string[] = [];

const normalizeAccount = (email: string | null | undefined): string => (email ?? "").trim().toLowerCase();

/**
 * The server's answer as the field's options: tag names, most used first.
 *
 * The server already orders its answer (most used, then alphabetical). This re-reads the
 * counts rather than trusting the order, keeps the server's order between two tags used
 * equally often, and folds the answer into what the field itself stores: the field trims
 * every tag it saves, so " VIP" and "VIP" are one suggestion here (their counts added) and a
 * blank one is none. An answer that is not a list — an older server, a proxy's error page
 * read as JSON — is no suggestions, never a crash in a form.
 */
export function orderTagSuggestions(counts: REST_schema.CrmContactTagCount[] | null | undefined): string[] {
  const totals = new Map<string, number>();

  for (const entry of Array.isArray(counts) ? counts : []) {
    const tag = typeof entry?.tag === "string" ? entry.tag.trim() : "";

    if (!tag) continue;

    const count = typeof entry.count === "number" && Number.isFinite(entry.count) ? entry.count : 0;

    totals.set(tag, (totals.get(tag) ?? 0) + count);
  }

  // A Map lists its keys in first-seen order; the index is the explicit tie-break, so the
  // result does not depend on the engine's sort being stable.
  return Array.from(totals.entries())
    .map(([tag, count], index) => ({ tag, count, index }))
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .map(({ tag }) => tag);
}

/**
 * The suggestions for a tag field whose list is open (`isOpen`) or closed. Returns the tag
 * names to offer, most used first: empty until the first answer, and whatever the field
 * already had after an ask that failed. There is deliberately no "loading" beside them.
 */
export function useContactTagSuggestions(isOpen: boolean): string[] {
  const { contactsTags } = useWebotServer();
  const { user } = useAuth();
  const account = normalizeAccount(user?.email);

  // The last answer this field received, stamped with the account it was given to.
  const [held, setHeld] = useState<{ account: string; tags: string[] } | null>(null);

  // `useWebotServer()` rebuilds its functions on every render. Through the ref the effect
  // below is keyed on the opening alone, so a re-render of an open field never asks twice.
  const fetcher = useRef(contactsTags);
  fetcher.current = contactsTags;

  useEffect(() => {
    if (!isOpen || !account) return undefined;

    // One ask per opening. The cleanup ends the opening — the list closed, the field
    // unmounted, or the account changed — and whatever lands after it is not this field's.
    let isThisOpening = true;

    // Called inside `then` on purpose: a field rendered where the API client is missing gets
    // the context's empty default, and calling `undefined` in an effect would take the whole
    // form down. There it is a failed ask like any other.
    Promise.resolve()
      .then(() => fetcher.current())
      .then((response) => {
        if (isThisOpening) setHeld({ account, tags: orderTagSuggestions(response?.tags) });
      })
      .catch((error) => {
        // Quiet on purpose: suggestions are a convenience on a free-text field, so a failure
        // costs the realtor nothing they need. The next opening asks again.
        // eslint-disable-next-line no-console
        console.error("useContactTagSuggestions: could not load the tag suggestions", error);
      });

    return () => {
      isThisOpening = false;
    };
  }, [isOpen, account]);

  // Compared during render, not reset in an effect: after a sign-in as someone else the
  // previous account's tags must not be offered for even one frame.
  return held?.account === account ? held.tags : NO_SUGGESTIONS;
}
